/**
 * Menu déroulant réutilisable.
 *
 * - Refermé au clic extérieur et à Echap
 * - Navigable au clavier (flèches haut/bas)
 * - Zones tactiles ≥ 40px
 * - Positionné en `fixed` depuis son ancre : une colonne du kanban ou un
 *   tableau qui défile ne le rogne pas. Il s'ouvre vers le haut quand la place
 *   manque en bas.
 */

import { useEffect, useLayoutEffect, useRef, useCallback, useState, type ReactNode } from 'react';

interface DropdownMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  children: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  /** Wider panel, for the notification list. */
  width?: string;
}

const GAP = 6;
const MARGIN = 8;

export function DropdownMenu({ open, onClose, anchorRef, children, align = 'right', className, width }: DropdownMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<React.CSSProperties>({ visibility: 'hidden' });

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;
    const a = anchor.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = align === 'right' ? a.right - m.width : a.left;
    left = Math.min(Math.max(MARGIN, left), vw - m.width - MARGIN);

    const below = a.bottom + GAP;
    const fitsBelow = below + m.height <= vh - MARGIN;
    const top = fitsBelow ? below : Math.max(MARGIN, a.top - GAP - m.height);

    setPosition({ position: 'fixed', top, left, right: 'auto', visibility: 'visible' });
  }, [anchorRef, align]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition({ visibility: 'hidden' });
      return;
    }
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, place]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  // Close on Escape, navigate with arrows
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      anchorRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (!items || items.length === 0) return;
      const current = document.activeElement as HTMLElement;
      const idx = Array.from(items).indexOf(current);
      const next = e.key === 'ArrowDown'
        ? (idx + 1) % items.length
        : (idx - 1 + items.length) % items.length;
      items[next]?.focus();
    }
  }, [onClose, anchorRef]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      role="menu"
      onKeyDown={handleKeyDown}
      className={`dropdown-menu ${className ?? ''}`}
      style={{ ...position, width }}
    >
      {children}
    </div>
  );
}

interface DropdownItemProps {
  onClick?: () => void;
  children: ReactNode;
  danger?: boolean;
  icon?: ReactNode;
}

export function DropdownItem({ onClick, children, danger, icon }: DropdownItemProps) {
  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  return (
    <button
      type="button"
      role="menuitem"
      className={`dropdown-item ${danger ? 'dropdown-item--danger' : ''}`}
      onClick={handleClick}
    >
      {icon}
      {children}
    </button>
  );
}
